import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useGlobalAutoRefreshStore, useAutoRefreshTrigger } from '../stores/useGlobalAutoRefreshStore';
import './TradingConfiguration.css';
import DashboardBottomLogo from './DashboardBottomLogo';
import SectionTitle from './shared/SectionTitle';
import StandaloneSection from './shared/StandaloneSection';
import {
  fetchGlobalConfigs,
  fetchAllProductConfigs,
  upsertGlobalConfig,
  upsertProductConfig,
  deleteGlobalConfig,
  deleteProductConfig,
  ConfigItem,
  ProductConfigItem
} from '../api/config';
import ConfigModal, { ConfigModalData } from './ConfigModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';

type TabType = 'global' | 'product';

const TradingConfiguration: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('global');
  const [searchQuery, setSearchQuery] = useState('');
  const [modalData, setModalData] = useState<ConfigModalData | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ConfigItem | ProductConfigItem | null>(null);
  const queryClient = useQueryClient();
  const setActiveDashboard = useGlobalAutoRefreshStore((state) => state.setActiveDashboard);
  const refreshTrigger = useAutoRefreshTrigger('admin');

  // Set this dashboard as active when mounted, clear when unmounted
  useEffect(() => {
    setActiveDashboard('admin');
    return () => setActiveDashboard(null);
  }, [setActiveDashboard]);

  // Handle auto-refresh
  useEffect(() => {
    if (refreshTrigger > 0) {
      queryClient.invalidateQueries({ queryKey: ['globalConfigs'] });
      queryClient.invalidateQueries({ queryKey: ['productConfigs'] });
    }
  }, [refreshTrigger, queryClient]);

  // Fetch global configs
  const { data: globalConfigs, isLoading: globalLoading, error: globalError } = useQuery({
    queryKey: ['globalConfigs'],
    queryFn: fetchGlobalConfigs,
    refetchInterval: 5000, // Refetch every 5 seconds to update status
  });

  // Fetch product configs
  const { data: productConfigs, isLoading: productLoading, error: productError } = useQuery({
    queryKey: ['productConfigs'],
    queryFn: fetchAllProductConfigs,
    refetchInterval: 5000, // Refetch every 5 seconds to update status
  });

  // Mutations for global configs
  const upsertGlobalMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => upsertGlobalConfig(key, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['globalConfigs'] });
      setModalData(null);
    },
  });

  const deleteGlobalMutation = useMutation({
    mutationFn: (key: string) => deleteGlobalConfig(key),
    onMutate: async (key: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['globalConfigs'] });

      // Snapshot previous value
      const previousConfigs = queryClient.getQueryData<ConfigItem[]>(['globalConfigs']);

      // Optimistically update to mark as PENDING
      queryClient.setQueryData<ConfigItem[]>(['globalConfigs'], (old) => {
        if (!old) return old;
        return old.map((config) =>
          config.key === key ? { ...config, status: 'PENDING' as const } : config
        );
      });

      return { previousConfigs };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['globalConfigs'] });
    },
    onError: (_error, _key, context) => {
      if (context?.previousConfigs) {
        queryClient.setQueryData(['globalConfigs'], context.previousConfigs);
      }
    },
  });

  // Mutations for product configs
  const upsertProductMutation = useMutation({
    mutationFn: ({ productId, key, value }: { productId: string; key: string; value: string }) =>
      upsertProductConfig(productId, key, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productConfigs'] });
      setModalData(null);
    },
  });

  const deleteProductMutation = useMutation({
    mutationFn: ({ productId, key }: { productId: string; key: string }) => deleteProductConfig(productId, key),
    onMutate: async ({ productId, key }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['productConfigs'] });

      // Snapshot the previous value
      const previousConfigs = queryClient.getQueryData<ProductConfigItem[]>(['productConfigs']);

      // Optimistically update to mark as PENDING
      queryClient.setQueryData<ProductConfigItem[]>(['productConfigs'], (old) => {
        if (!old) return old;
        return old.map((config) =>
          config.productId === productId && config.key === key
            ? { ...config, status: 'PENDING' as const }
            : config
        );
      });

      return { previousConfigs };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productConfigs'] });
    },
    onError: (_error, _variables, context) => {
      if (context?.previousConfigs) {
        queryClient.setQueryData(['productConfigs'], context.previousConfigs);
      }
    },
  });

  const handleAddNew = () => {
    setModalData({
      type: activeTab,
      key: '',
      value: '',
      productId: activeTab === 'product' ? '' : undefined,
      isNew: true,
    });
  };

  const handleEdit = (config: ConfigItem | ProductConfigItem) => {
    if ('productId' in config) {
      setModalData({
        type: 'product',
        originalKey: config.key,
        originalProductId: config.productId,
        key: config.key,
        value: config.value,
        productId: config.productId,
        isNew: false,
      });
    } else {
      setModalData({
        type: 'global',
        originalKey: config.key,
        key: config.key,
        value: config.value,
        isNew: false,
      });
    }
  };

  const handleModalSave = (data: ConfigModalData) => {
    if (data.type === 'global') {
      upsertGlobalMutation.mutate({ key: data.key, value: data.value });
    } else if (data.productId) {
      upsertProductMutation.mutate({
        productId: data.productId,
        key: data.key,
        value: data.value,
      });
    }
  };

  const handleModalClose = () => {
    setModalData(null);
  };

  const handleDelete = (config: ConfigItem | ProductConfigItem) => {
    // Open styled confirmation modal instead of native confirm
    setDeleteTarget(config);
  };


  // Filter configs based on search query
  const filteredGlobalConfigs = globalConfigs?.filter((config) =>
    config.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
    config.value.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const filteredProductConfigs = productConfigs?.filter((config) =>
    config.productId.toLowerCase().includes(searchQuery.toLowerCase()) ||
    config.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
    config.value.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Get unique product IDs for the product dropdown
  const uniqueProductIds = Array.from(new Set(productConfigs?.map(c => c.productId) || [])).sort();

  const renderGlobalConfigsTable = () => {
    if (filteredGlobalConfigs.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">⚙️</div>
          <h3>No Global Configurations</h3>
          <p>Click "Add Configuration" to create a new global config</p>
        </div>
      );
    }

    return (
      <table className="config-table">
        <thead>
          <tr>
            <th style={{ width: '35%' }}>Key</th>
            <th style={{ width: '45%' }}>Value</th>
            <th style={{ width: '10%' }}>Status</th>
            <th style={{ width: '10%' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredGlobalConfigs.map((config) => {
            const isDeleting = config.status === 'PENDING' && deleteGlobalMutation.isPending;
            return (
              <tr key={config.key} className={isDeleting ? 'deleting' : ''}>
                <td>{config.key}</td>
                <td>{config.value}</td>
                <td>
                  <span className={`status-badge ${config.status.toLowerCase()}`}>
                    {config.status}
                  </span>
                </td>
                <td>
                  <div className="config-actions-cell">
                    <button 
                      className="action-btn" 
                      onClick={() => handleEdit(config)}
                      disabled={isDeleting}
                    >
                      Edit
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(config)}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderProductConfigsTable = () => {
    if (filteredProductConfigs.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">🔧</div>
          <h3>No Product Configurations</h3>
          <p>Click "Add Configuration" to create a new product-specific config</p>
        </div>
      );
    }

    return (
      <table className="config-table">
        <thead>
          <tr>
            <th style={{ width: '20%' }}>Product ID</th>
            <th style={{ width: '30%' }}>Key</th>
            <th style={{ width: '35%' }}>Value</th>
            <th style={{ width: '8%' }}>Status</th>
            <th style={{ width: '7%' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredProductConfigs.map((config) => {
            const isDeleting = config.status === 'PENDING' && deleteProductMutation.isPending;
            return (
              <tr key={`${config.productId}-${config.key}`} className={isDeleting ? 'deleting' : ''}>
                <td>{config.productId}</td>
                <td>{config.key}</td>
                <td>{config.value}</td>
                <td>
                  <span className={`status-badge ${config.status.toLowerCase()}`}>
                    {config.status}
                  </span>
                </td>
                <td>
                  <div className="config-actions-cell">
                    <button 
                      className="action-btn" 
                      onClick={() => handleEdit(config)}
                      disabled={isDeleting}
                    >
                      Edit
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(config)}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const isLoading = activeTab === 'global' ? globalLoading : productLoading;
  const error = activeTab === 'global' ? globalError : productError;

  return (
    <div className="trading-configuration">
      <div className="dashboard-header">
        <SectionTitle title="Trading Configuration" />
      </div>

      <div className="config-tabs">
        <button
          className={`config-tab ${activeTab === 'global' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('global');
            setModalData(null);
            setSearchQuery('');
          }}
        >
          Global Configuration
        </button>
        <button
          className={`config-tab ${activeTab === 'product' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('product');
            setModalData(null);
            setSearchQuery('');
          }}
        >
          Product Configuration
        </button>
      </div>

      <div className="dashboard-content">
        <StandaloneSection
          title={activeTab === 'global' ? 'GLOBAL SETTINGS' : 'PRODUCT SETTINGS'}
          actions={
            <button
              className="add-config-btn"
              onClick={handleAddNew}
              disabled={!!modalData}
            >
              + Add Configuration
            </button>
          }
        >
          {error && (
            <div className="error-message">
              Error loading configurations: {error.message}
            </div>
          )}

          <div className="config-search">
            <input
              type="text"
              placeholder="Search configurations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {isLoading ? (
            <div className="loading-state">
              <p>Loading configurations...</p>
            </div>
          ) : (
            <div className="config-table-container">{activeTab === 'global' ? renderGlobalConfigsTable() : renderProductConfigsTable()}</div>
          )}
        </StandaloneSection>
      </div>

      {modalData && (
        <ConfigModal
          data={modalData}
          uniqueProductIds={uniqueProductIds}
          onSave={handleModalSave}
          onClose={handleModalClose}
          isSaving={
            modalData.type === 'global'
              ? upsertGlobalMutation.isPending
              : upsertProductMutation.isPending
          }
        />
      )}
      {deleteTarget && (
        <DeleteConfirmationModal
          config={deleteTarget}
          isDeleting={
            ('productId' in deleteTarget)
              ? deleteProductMutation.isPending
              : deleteGlobalMutation.isPending
          }
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => {
            if ('productId' in deleteTarget) {
              deleteProductMutation.mutate({ productId: deleteTarget.productId, key: deleteTarget.key });
            } else {
              deleteGlobalMutation.mutate(deleteTarget.key);
            }
            setDeleteTarget(null);
          }}
        />
      )}

      <DashboardBottomLogo />
    </div>
  );
};

export default TradingConfiguration;
